import { cloudSaves } from "../emulator/cloudSaves";
import { here } from "../desktop/desktop";
import { isEjsSystem } from "../emulator/ejs";
import { ejsStates } from "../emulator/ejsStates";
import { saveFileName, stateFileName } from "../emulator/saves";
import { slotStore } from "../emulator/slots";
import { loadRecords, saveRecords } from "../emulator/syncRecords";
import { deleteIdbfs, readIdbfs, writeIdbfs } from "./idbfs";
import type { LibraryGame, LibrarySlot } from "./myLibrary";

// A game's save files from the library page, with no emulator running:
// straight into browser storage, which the cores read on their next start,
// and the cloud copy where there is one. Cloud sync takes it from there.

const isEjs = (game: LibraryGame) => !!game.system && isEjsSystem(game.system);
const mgbaState = (file: string) => `/data/states/${file}`;

/** The in-game save: whichever copy is newer, this browser's or the cloud's. */
export async function readGameSave(game: LibraryGame): Promise<Uint8Array> {
  const local = game.localSave;
  if (local && (game.cloudSaveAt === null || local.updatedAt >= game.cloudSaveAt)) return local.bytes;
  return cloudSaves.download(saveFileName(game.key));
}

/**
 * Replaces the in-game save. mGBA's goes in /data/saves; EmulatorJS names its
 * own save file, so its save can only be replaced where it already has one,
 * and otherwise goes to the cloud copy for sync to bring down.
 */
export async function writeGameSave(game: LibraryGame, bytes: Uint8Array, signedIn: boolean): Promise<"here" | "cloud"> {
  if (game.local && !isEjs(game)) {
    if (await writeIdbfs("/data", `/data/saves/${saveFileName(game.name)}`, bytes)) return "here";
  } else if (game.local && game.localSave) {
    if (await writeIdbfs("/data/saves", game.localSave.path, bytes)) return "here";
  }
  if (!signedIn) throw new Error(`Play the game once in ${here} first, or sign in to upload it to the cloud.`);
  await cloudSaves.upload(saveFileName(game.key), bytes);
  return "cloud";
}

/**
 * Deletes the in-game save everywhere it is: this device's copy, the cloud
 * copy and what sync remembers, so sync doesn't bring either back.
 */
export async function deleteGameSave(game: LibraryGame, signedIn: boolean) {
  if (game.localSave) {
    if (isEjs(game)) await deleteIdbfs("/data/saves", [game.localSave.path]);
    else await deleteIdbfs("/data", [game.localSave.path]);
  }
  if (game.cloudSaveAt !== null && signedIn) await cloudSaves.remove(saveFileName(game.key));
  const records = loadRecords(game.name);
  const file = saveFileName(game.name);
  if (file in records) {
    delete records[file];
    saveRecords(game.name, records);
  }
}

/** A slot's state file: this browser's copy, or the cloud's. */
export async function readSlot(game: LibraryGame, slot: LibrarySlot): Promise<Uint8Array> {
  const file = stateFileName(game.name, slot.slot);
  if (slot.local) {
    if (isEjs(game)) {
      const state = await ejsStates.get(file);
      if (state) return state.bytes;
    } else {
      const found = (await readIdbfs("/data", mgbaState(file))).find((f) => f.path === mgbaState(file));
      if (found) return found.bytes;
    }
  }
  return cloudSaves.download(file);
}

/**
 * Writes a state file into a slot (new or existing) and registers the slot.
 * An EmulatorJS state never goes to the cloud: the player wouldn't take it
 * from there (its states stay in the browser).
 */
export async function writeSlot(game: LibraryGame, slot: number, bytes: Uint8Array, signedIn: boolean) {
  const file = stateFileName(game.name, slot);
  if (game.local) {
    if (isEjs(game)) await ejsStates.put(file, bytes);
    else if (!(await writeIdbfs("/data", mgbaState(file), bytes))) throw new Error("Could not write the state.");
  } else if (isEjs(game)) {
    throw new Error(`Add the game to ${here} first: states for this console stay in the browser.`);
  } else if (signedIn) {
    await cloudSaves.upload(file, bytes);
  } else {
    throw new Error(`Add the game to ${here} first, or sign in to upload it to the cloud.`);
  }
  const slots = slotStore.list(game.name);
  if (!slots.includes(slot)) slotStore.setList(game.name, [...slots, slot].sort((a, b) => a - b));
  slotStore.setStats(game.name, slot, { at: Date.now(), trainer: null, playTime: null });
}

/**
 * Deletes a slot everywhere it is: this browser's copy, the cloud copy, what
 * sync remembers about it, and the slot itself with its name and stats.
 */
export async function deleteSlot(game: LibraryGame, slot: LibrarySlot) {
  const file = stateFileName(game.name, slot.slot);
  if (slot.local) {
    if (isEjs(game)) await ejsStates.remove(file);
    else await deleteIdbfs("/data", [mgbaState(file)]);
  }
  if (slot.inCloud) await cloudSaves.remove(file);
  const records = loadRecords(game.name);
  if (file in records) {
    delete records[file];
    saveRecords(game.name, records);
  }
  if (slotStore.known(game.name)) {
    slotStore.forget(game.name, slot.slot);
    slotStore.setList(
      game.name,
      slotStore.list(game.name).filter((n) => n !== slot.slot),
    );
  }
}
